/**
 * 证据 / 任务状态统一状态层（EvidenceStore）
 * 对应优化系统设计方案 §3.2。所有提交 / 绑定 / 签收 / 状态变更的唯一入口，禁止裸 alert。
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type {
  BindRecord,
  EvidenceRecord,
  LoadingRecord,
  QcAnomaly,
  SignatureReceipt,
  TaskStatus,
} from '@bluplanet/shared';
import { getJSON, setJSON } from '@/utils/storage';
import { genId } from '@/utils/id';

/** 提交动作类型 */
export type SubmitType = 'BIND_IOT' | 'LOADING_SEAL' | 'SIGN_UNBOX' | 'QC_SUBMIT' | 'QC_ANOMALY';

/** 提交结果 */
export interface SubmitResult {
  ok: boolean;
  message: string;
}

interface EvidenceStoreValue {
  addEvidence: (record: EvidenceRecord) => void;
  getEvidence: (id: string) => EvidenceRecord | undefined;
  submitAction: (type: SubmitType, payload: unknown) => SubmitResult;
  updateTaskStatus: (taskNo: string, status: TaskStatus) => void;
  getTaskStatus: (taskNo: string) => TaskStatus | undefined;
}

const EvidenceContext = createContext<EvidenceStoreValue | undefined>(undefined);

/** 向指定 localStorage 数组追加一项 */
function appendToStore<T>(key: string, item: T): void {
  const arr = getJSON<T[]>(key, []);
  arr.push(item);
  setJSON(key, arr);
}

export function EvidenceProvider({ children }: { children: React.ReactNode }) {
  const [evidence, setEvidence] = useState<EvidenceRecord[]>(() => getJSON<EvidenceRecord[]>('evidence', []));
  const [tasks, setTasks] = useState<Record<string, TaskStatus>>(() =>
    getJSON<Record<string, TaskStatus>>('tasks', {}),
  );

  useEffect(() => {
    setJSON('evidence', evidence);
  }, [evidence]);

  useEffect(() => {
    setJSON('tasks', tasks);
  }, [tasks]);

  const addEvidence = useCallback((record: EvidenceRecord) => {
    setEvidence((prev) => [...prev, record]);
  }, []);

  const getEvidence = useCallback((id: string) => evidence.find((item) => item.id === id), [evidence]);

  const updateTaskStatus = useCallback((taskNo: string, status: TaskStatus) => {
    setTasks((prev) => ({ ...prev, [taskNo]: status }));
  }, []);

  const getTaskStatus = useCallback((taskNo: string) => tasks[taskNo], [tasks]);

  const submitAction = useCallback((type: SubmitType, payload: unknown): SubmitResult => {
    switch (type) {
      case 'BIND_IOT': {
        const p = (payload ?? {}) as Partial<BindRecord>;
        if (!p.deviceSN || !p.containerId || !p.photoEvidenceId) {
          return { ok: false, message: '绑定信息不完整：需 deviceSN、containerId、photoEvidenceId' };
        }
        const now = new Date().toISOString();
        const record: BindRecord = {
          deviceSN: p.deviceSN,
          containerId: p.containerId,
          bindTime: p.bindTime ?? now,
          gpsLocation: p.gpsLocation ?? { lat: 0, lng: 0 },
          photoEvidenceId: p.photoEvidenceId,
          operator: p.operator ?? '',
          status: 'BOUND',
          createdAt: now,
        };
        appendToStore('bind', record);
        return { ok: true, message: '设备已绑定柜体' };
      }
      case 'LOADING_SEAL': {
        const p = (payload ?? {}) as Partial<LoadingRecord>;
        if (!p.containerNo || !p.sealNumber) {
          return { ok: false, message: '装柜信息不完整：需 containerNo、sealNumber' };
        }
        const now = new Date().toISOString();
        const record: LoadingRecord = {
          containerNo: p.containerNo,
          sealNumber: p.sealNumber,
          loadingWeight: p.loadingWeight ?? 0,
          loadingTime: p.loadingTime ?? now,
          temperatureAtLoading: p.temperatureAtLoading ?? 0,
          videoEvidenceId: p.videoEvidenceId,
          sealPhotoEvidenceId: p.sealPhotoEvidenceId,
          createdAt: now,
          updatedAt: now,
        };
        appendToStore('loading', record);
        return { ok: true, message: '装柜记录已提交' };
      }
      case 'SIGN_UNBOX': {
        const p = (payload ?? {}) as Partial<SignatureReceipt>;
        if (!p.containerNo || !p.signatureDataUrl) {
          return { ok: false, message: '签收信息不完整：需 containerNo、signatureDataUrl' };
        }
        const record: SignatureReceipt = {
          id: p.id ?? genId('rcpt'),
          containerNo: p.containerNo,
          signerName: p.signerName ?? '',
          signerRole: p.signerRole ?? '',
          signatureDataUrl: p.signatureDataUrl,
          ataTime: p.ataTime ?? '',
          portCode: p.portCode ?? '',
          berthNumber: p.berthNumber ?? '',
          unboxVideoEvidenceId: p.unboxVideoEvidenceId,
          damagePhotoEvidenceIds: p.damagePhotoEvidenceIds ?? [],
          signedAt: p.signedAt ?? new Date().toISOString(),
        };
        appendToStore('receipt', record);
        return { ok: true, message: '签收单已生成' };
      }
      case 'QC_ANOMALY': {
        const p = (payload ?? {}) as Partial<QcAnomaly>;
        if (!p.containerNo || !p.type) {
          return { ok: false, message: '异常上报不完整：需 containerNo、type' };
        }
        const record: QcAnomaly = {
          id: p.id ?? genId('anom'),
          containerNo: p.containerNo,
          nodeId: p.nodeId ?? 0,
          nodeName: p.nodeName ?? '',
          type: p.type,
          remark: p.remark ?? '',
          createdAt: new Date().toISOString(),
        };
        appendToStore('anomaly', record);
        return { ok: true, message: '异常已上报' };
      }
      case 'QC_SUBMIT': {
        // 质检报告上传：MVP 无独立报告实体，localStorage 约定未包含 :qc；
        // 此处扩展 :qc 以持久化报告、避免静默丢数据（见实现汇总说明）。
        if (!payload || typeof payload !== 'object') {
          return { ok: false, message: '质检报告为空' };
        }
        appendToStore('qc', {
          ...(payload as Record<string, unknown>),
          submittedAt: new Date().toISOString(),
        });
        return { ok: true, message: '质检报告已提交' };
      }
      default:
        return { ok: false, message: '未知操作类型' };
    }
  }, []);

  const value: EvidenceStoreValue = {
    addEvidence,
    getEvidence,
    submitAction,
    updateTaskStatus,
    getTaskStatus,
  };

  return <EvidenceContext.Provider value={value}>{children}</EvidenceContext.Provider>;
}

/** 获取证据 / 任务状态操作（须在 EvidenceProvider 内部使用） */
export function useEvidenceStore(): EvidenceStoreValue {
  const ctx = useContext(EvidenceContext);
  if (!ctx) throw new Error('useEvidenceStore 必须在 EvidenceProvider 内部使用');
  return ctx;
}
